// Thẻ của hộp hướng dẫn trang Ca vận hành — MỘT nguồn, buộc vào nhãn thật.
//
// Nhãn lấy thẳng từ `BRANCH_SHIFT_TAB_LABEL` của màn, và danh sách khoá khai
// `satisfies` theo đúng khoá của từ điển đó: đổi khoá thẻ trên màn là lỗi kiểu
// NGAY LÚC BUILD, không phải đợi ai mở hộp lên mới thấy bài chỉ tới một thẻ
// không còn tồn tại.

import type { BRANCH_SHIFT_TAB_LABEL } from "@/features/branch-shift/labels";
import type { Audience } from "./audience";
import type { StatusTone } from "./GuideSections";
import type { GuideFlow, MultiTabGuideConfig } from "./multi-tab-guides";

/** Id trang trong `nav-items.ts`. Tra bằng hằng số, không gõ chuỗi lần hai. */
export const BRANCH_SHIFT_GUIDE_ID = "branch_shifts";

/** Thứ tự thẻ trong hộp = thứ tự thẻ trên màn. */
export const BRANCH_SHIFT_GUIDE_TAB_ORDER = [
  "today",
  "chain",
  "week",
  "incidents",
  "templates",
  "makeupFaces",
] as const satisfies readonly (keyof typeof BRANCH_SHIFT_TAB_LABEL)[];

export type BranchShiftGuideTabKey = (typeof BRANCH_SHIFT_GUIDE_TAB_ORDER)[number];

/**
 * Nhóm nghề nào MỞ ĐƯỢC thẻ nào trên màn thật.
 *
 * Dữ liệu kỹ thuật để hộp bày đúng số thẻ, KHÔNG phải nội dung hiển thị. Khai
 * TAY chứ không suy từ "thẻ này có bài của họ" — xem chú thích ở
 * `multi-tab-guides.ts` để biết vì sao hai thứ đó lệch nhau theo hướng nguy
 * hiểm.
 *
 * Nguồn: `features/branch-shift/permissions.ts` + bảng hành động của máy chủ
 * (`internal/platform/auth/actions.go`). Đổi rào bên đó thì sửa cả bảng này:
 *
 *   today      branchshift.read      — gần như mọi vai vào được trang
 *   chain/week branchshift.plan      — admin, giám đốc, trưởng cửa hàng
 *   incidents  incident.read         — cùng tập với branchshift.read
 *   templates  shifttemplate.write   — chỉ admin và giám đốc
 *   makeupFaces order.makeupfaces.approve — vai chuỗi + admin/giám đốc
 */
export const THE_AI_MO_DUOC: Record<BranchShiftGuideTabKey, Audience[]> = {
  // Ca hôm nay: người trực ca thật (quầy, chụp, trang điểm) cùng mọi vai đọc
  // được trang. Đây là thẻ làm việc chính, không rào.
  today: [
    "reception",
    "sale",
    "photo",
    "makeup",
    "assistant",
    "retouch",
    "cskh",
    "marketing",
    "accounting",
    "qc",
    "lead",
    "exec",
  ],
  // Hai thẻ điều hành nhiều cơ sở: `branchshift.plan` chỉ mở cho vai chuỗi và
  // giám đốc, nên nhân sự làm ca KHÔNG thấy.
  chain: ["lead", "exec"],
  week: ["lead", "exec"],
  // Sự cố: cùng tập với thẻ đầu — ai làm ca cũng cần ghi được chuyện bất thường.
  incidents: [
    "reception",
    "sale",
    "photo",
    "makeup",
    "assistant",
    "retouch",
    "cskh",
    "marketing",
    "accounting",
    "qc",
    "lead",
    "exec",
  ],
  // Mẫu & phụ cấp cố ý HẸP NHẤT: `shifttemplate.write` chỉ admin/giám đốc.
  // Trưởng cửa hàng KHÔNG có — đừng gộp chung với hai thẻ điều hành ở trên.
  templates: ["exec"],
  // Duyệt số mặt: cùng tập với hai thẻ điều hành, nhưng là một cổng RIÊNG
  // (`order.makeupfaces.approve`) — trưởng phòng cố ý KHÔNG có, dù vai đó ghi
  // được sổ công doanh nghiệp (người chốt 25/09/2026). `audiencesOf` gộp
  // trưởng phòng vào nhóm `lead` cùng vai chuỗi nên hộp không phân biệt được
  // hai người đó; hệ quả đã chấp nhận: trưởng phòng đọc được bài của một thẻ
  // họ không mở được, giống hệt cách hộp đối xử với các thẻ điều hành khác.
  makeupFaces: ["lead", "exec"],
};

/** Màu nhãn trạng thái, khớp badge trên màn thật. */
const TONE_THEO_NHAN: Record<string, StatusTone> = {
  "Đang mở": "success",
  "Ảnh đạt": "success",
  "Đã xác minh": "success",
  "Đã đóng": "info",
  "Đã nắm": "info",
  "Chờ mở": "warning",
  "Đã xử lý": "warning",
  "Chưa xếp": "warning",
  "Chưa bật ca vận hành": "warning",
  "Chờ duyệt": "warning",
  "Đã duyệt": "success",
  "Đã trả lại": "danger",
  "Ảnh chưa đạt": "danger",
};

/**
 * Vòng đời một ca của cơ sở.
 *
 * Năm trong sáu thẻ của màn là năm lát cắt của cùng một ca: chỗ làm ca, chỗ
 * nhìn nhiều cơ sở, chỗ xếp người, chỗ ghi chuyện bất thường, chỗ định bảng
 * kiểm. Đứng trong một thẻ thì không thấy được mạch đó.
 *
 * Thẻ "Duyệt số mặt" cố ý ĐỨNG NGOÀI mạch này: nó soát một con số của ĐƠN
 * HÀNG, không phải một chặng của ca. Nhét vào cho đủ sáu là bịa ra một thứ tự
 * không có thật.
 */
const BRANCH_SHIFT_FLOW: GuideFlow = {
  heading: "Một ca đi qua đâu",
  intro:
    "Các thẻ của trang là những lát cắt của cùng một ca. Chặng tô đậm là chỗ thẻ bạn đang mở nhìn vào.",
  stages: [
    {
      ten: "Xếp người",
      tone: "info",
      ke: "Trước đó có người xếp ai trực ca nào cho tuần tới; chưa xếp thì ca hôm đó bỏ trống.",
      the: ["week"],
    },
    {
      ten: "Mở ca",
      tone: "warning",
      ke: "Đầu ca làm bảng kiểm mở ca, chụp ảnh những mục cần ảnh, rồi bấm mở ca.",
      the: ["today", "chain"],
    },
    {
      ten: "Đóng ca",
      tone: "success",
      ke: "Cuối ca làm bảng kiểm đóng ca, ghi bàn giao cho ca sau rồi đóng; ca đã đóng vẫn mở ra xem lại được.",
      the: ["today", "chain"],
    },
    {
      ten: "Sự cố trong ca",
      tone: "danger",
      nhanh: true,
      ke: "Nhánh rẽ: chuyện bất thường xảy ra lúc nào thì ghi lúc đó, và sống tiếp cả sau khi ca đã đóng.",
      the: ["incidents"],
    },
  ],
};

export const BRANCH_SHIFT_GUIDE: MultiTabGuideConfig = {
  tabOrder: BRANCH_SHIFT_GUIDE_TAB_ORDER,
  theAiMoDuoc: THE_AI_MO_DUOC,
  tone: TONE_THEO_NHAN,
  flow: BRANCH_SHIFT_FLOW,
};
